
import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { NETWORK } from '@/src/helpers';

interface NetworkContextType {
  isMainnet: boolean;
  isTestnet: boolean;
  network: NETWORK;
  setNetwork: (network: NETWORK) => void;
  isTransitioning: boolean;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export function NetworkProvider({ children }: { children: ReactNode }) {
  const [network, setNetworkState] = useState<NETWORK>('xrpl:1');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const isMainnet = network === 'xrpl:0';
  const isTestnet = network === 'xrpl:1';

  // Wrapped setNetwork to handle transition state
  const setNetwork = useCallback((newNetwork: NETWORK) => {
    if (newNetwork !== network) {
      console.debug('[network] Switching network from', network, 'to', newNetwork);
      setIsTransitioning(true);
      setNetworkState(newNetwork);
    }
  }, [network]);

  // Reset transition state after network change completes
  useEffect(() => {
    if (isTransitioning) {
      // Allow time for XRPL client to connect and data to load
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        console.debug('[network] Network switch completed');
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  return (
    <NetworkContext.Provider
      value={{ network, setNetwork, isMainnet, isTestnet, isTransitioning }}
    >
      {children}
    </NetworkContext.Provider>
  );
}

export function useNetwork() {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
}
